import { AxiosInstance } from "axios";
import { APIResponse } from "@/core/types/api";
import { Farm } from "../farm/types/farm.type";
import { CaseStatus } from "./case.type";
import { Message } from "../messages/message.type";

export interface Case {
  _id: string;
  title: string;
  description: string;
  caseType: "CROP" | "LIVESTOCK";
  farmId: string | Farm;
  productionId?: string;
  farmerId: Record<string, any>;
  status: CaseStatus;
  assignedOfficerId?: Record<string, any>;
  images: string[];
  video?: string;
  audio?: string;
  transcript?: string;
  aiResponse?: string;
  aiResponseSentAt?: string;
  aiProcessingStatus?: "PENDING" | "TRANSCRIBING" | "ANSWERING" | "SENT" | "PENDING_REVIEW" | "ESCALATED" | "NO_SPEECH" | "LOW_CONFIDENCE" | "NO_PHONE" | "FAILED";
  aiErrorReason?: string;
  aiDraftReply?: string;
  aiDraftReason?: string;
  aiDraftCreatedAt?: string;
  aiEscalationUnreachable?: boolean;
  aiEscalationUnreachableAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CaseAuditChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface CaseAudit {
  _id: string;
  caseId: string;
  editedBy:
    | {
        _id: string;
        fullName?: string;
        email?: string;
        role?: string;
      }
    | string;
  editedAt: string;
  action: "UPDATE";
  changes: CaseAuditChange[];
  createdAt: string;
  updatedAt: string;
}

export type CreateCaseDto = {
  title: string;
  description: string;
  caseType: "CROP" | "LIVESTOCK";
  farmId: string;
  productionId: string;
  farmerId: string;
  status: CaseStatus;
  assignedOfficerId?: string;
  images: string[];
  video?: string;
  audio?: string;
};
export type UpdateCaseDto = Partial<CreateCaseDto>;

export function caseService(api: AxiosInstance) {
  return {
    getAll(
      basePath = "/farmer/cases",
      params?: Record<string, string>,
    ): Promise<APIResponse<Case[]>> {
      return api.get(basePath, { params }).then((res) => res.data);
    },

    getById(id: string, basePath = "/farmer/cases"): Promise<APIResponse<Case>> {
      return api.get(`${basePath}/${id}`).then((res) => res.data);
    },

    create(data: CreateCaseDto, basePath = "/farmer/cases"): Promise<APIResponse<Case>> {
      return api.post(basePath, data).then((res) => res.data);
    },

    update(
      id: string,
      data: UpdateCaseDto,
      basePath = "/farmer/cases",
    ): Promise<APIResponse<Case>> {
      return api.put(`${basePath}/${id}`, data).then((res) => res.data);
    },

    getHistory(
      caseId: string,
      basePath = "/farmer/cases",
    ): Promise<APIResponse<CaseAudit[]>> {
      return api.get(`${basePath}/${caseId}/history`).then((res) => res.data);
    },

    getMessages(
      caseId: string,
      basePath = "/farmer/cases",
    ): Promise<APIResponse<Message[]>> {
      return api.get(`${basePath}/${caseId}/messages`).then((res) => res.data);
    },

    sendMessage(
      caseId: string,
      payload: { content?: string; type?: string; audioUrl?: string },
      basePath = "/farmer/cases",
    ): Promise<APIResponse<Message | null>> {
      return api
        .post(`${basePath}/${caseId}/messages`, payload)
        .then((res) => res.data);
    },

    resolveAiDraft(
      caseId: string,
      content: string,
      basePath = "/officer/cases",
    ): Promise<APIResponse<Message>> {
      return api
        .patch(`${basePath}/${caseId}/ai-draft`, { content })
        .then((res) => res.data);
    },
  };
}
